import { type Thread, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/EmptyState';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { getArchiveErrorDescription } from '@/features/chat/archive-error';
import { trackEvent } from '@/utils/analytics';

import {
  archivedChatSearchId,
  settingsItemDomId,
} from '../settings-search-dom';
import { SettingsSection } from '../settings-section';

const formatLastUpdated = (date: Date) =>
  date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const ArchivedChatRow = observer(({ thread }: { thread: Thread }) => {
  const { projects, threads } = useStores();
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const lastUpdated = thread.updatedAt ?? thread.createdAt;
  const project = thread.projectId ? projects.getById(thread.projectId) : null;
  const metadata = [
    lastUpdated ? formatLastUpdated(lastUpdated) : null,
    project?.name,
  ]
    .filter(Boolean)
    .join(' • ');

  const handleUnarchive = async () => {
    setIsUnarchiving(true);
    try {
      await threads.unarchiveThread(thread);
      trackEvent('CHAT_UNARCHIVED', { chatId: thread.id });
    } catch (error) {
      toast.error('Failed to unarchive chat', {
        description: getArchiveErrorDescription(error, 'unarchive'),
      });
    } finally {
      setIsUnarchiving(false);
    }
  };

  return (
    <XStack
      role="listitem"
      // anchors the row for search deep links
      id={settingsItemDomId(archivedChatSearchId(thread.id))}
      className="h-16 items-center justify-between gap-4 rounded-lg border border-border bg-surface px-4 py-2.5 transition-colors hover:bg-surface-hover"
    >
      <YStack className="min-w-0 flex-1 gap-2">
        <p className="truncate text-sm leading-none text-foreground">
          {thread.title || 'Untitled'}
        </p>
        {metadata && (
          <p className="truncate text-sm leading-none text-foreground-muted">
            {metadata}
          </p>
        )}
      </YStack>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleUnarchive}
        isLoading={isUnarchiving}
        loadingText="Restoring"
        className="shrink-0 rounded-lg px-4"
      >
        Unarchive
      </Button>
    </XStack>
  );
});

const ArchivedChatList = observer(() => {
  const { threads } = useStores();
  const archived = threads.archivedChats;

  if (archived.length === 0) {
    return (
      <Empty.Root>
        <Empty.Media variant="icon">
          <Icons.archive />
        </Empty.Media>
        <Empty.Header>
          <Empty.Title>Nothing archived yet</Empty.Title>
          <Empty.Description>
            Chats you archive show up here, ready to restore whenever you need
            them.
          </Empty.Description>
        </Empty.Header>
      </Empty.Root>
    );
  }

  return (
    <YStack role="list" className="w-full gap-3">
      {archived.map((thread) => (
        <ArchivedChatRow key={thread.id} thread={thread} />
      ))}
    </YStack>
  );
});

export const ArchivedChatsSettings = observer(() => {
  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Archived Chats</SettingsSection.Title>
      </SettingsSection.Header>
      <ArchivedChatList />
    </SettingsSection.Root>
  );
});
